import { Component, Vue } from 'vue-property-decorator'
import api from '@/helpers/fetch'
import Loader from '@/components/Loader/index.vue'
import vSelect from 'vue-select'

import {
	Chart,
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'

import type { LegendItem, LegendElement, ChartEvent } from 'chart.js'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faFilePdf, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import MessageErrorAlert from '@/components/edit/MessageErrorAlert/index.vue'
import { _DeepPartialObject } from 'chart.js/types/utils'

library.add(faFilePdf, faCircleInfo)

Chart.register(
	LineController,
	LineElement,
	PointElement,
	LinearScale,
	CategoryScale,
	Title,
	Tooltip,
	Legend,
)

@Component({
	components: {
		Loader,
		vSelect,
		MessageErrorAlert,
	},
})
export default class ThermometerTimeLine extends Vue {
	chart: Chart | null = null
	rawData: any[] = []
	selectedCompanies: Array<{ name: string }> = []
	selectedSubjects: Array<{ name: string }> = []
	loading = false
	alert = false
	maxCompanies = 5
	maxSubjects = 5

	repercussaoGeral = [
		{
			date: '2012-05-10',
			description: 'Decisão do STF sobre constitucionalidade da Lei',
		},
		{
			date: '2013-08-22',
			description: 'Liminar que suspendeu efeitos da Medida Provisória',
		},
		{
			date: '2014-01-15',
			description: 'Decisão histórica sobre direitos trabalhistas',
		},
		{
			date: '2015-03-15',
			description: 'Decisão do STF sobre reforma previdenciária',
		},
		{
			date: '2016-06-30',
			description: 'Liminar que barrou aumento de impostos',
		},
		{
			date: '2017-04-12',
			description: 'Decisão do STF sobre prisão em segunda instância',
		},
		{
			date: '2018-07-20',
			description: 'Liminar que suspendeu processo de privatização',
		},
		{
			date: '2019-02-05',
			description: 'Decisão do STF sobre liberdade de expressão',
		},
		{
			date: '2020-03-18',
			description: 'Liminar sobre medidas sanitárias na pandemia',
		},
		{
			date: '2020-11-05',
			description: 'Decisão do STF sobre competência legislativa',
		},
		{
			date: '2021-09-01',
			description: 'Decisão sobre marco regulatório da internet',
		},
		{
			date: '2022-05-25',
			description: 'Liminar que garantiu direitos indígenas',
		},
		{
			date: '2023-01-10',
			description: 'Decisão do STF sobre tributação de dividendos',
		},
		{
			date: '2023-08-15',
			description: 'Liminar sobre regulamentação de trabalho remoto',
		},
		{
			date: '2024-02-20',
			description: 'Decisão histórica sobre proteção de dados',
		},
	]

	options = {
		calc_subject: [] as Array<any>,
		company_name: [] as Array<any>,
	}

	companyColors: Map<string, string> = new Map()
	subjectColors: Map<string, string> = new Map()

	colorPalette = [
		'255, 99, 132', // Vermelho
		'54, 162, 235', // Azul
		'255, 206, 86', // Amarelo
		'75, 192, 192', // Ciano
		'153, 102, 255', // Roxo
		'255, 159, 64', // Laranja
		'199, 199, 199', // Cinza
		'83, 102, 255', // Azul royal
		'40, 159, 64', // Verde
		'210, 99, 132', // Rosa
		'100, 102, 255', // Azul médio
		'255, 99, 72', // Vermelho claro
		'54, 162, 135', // Verde água
		'255, 156, 86', // Laranja claro
		'175, 192, 192', // Ciano claro
		'153, 152, 255', // Lilás
	]

	async mounted() {
		await this.loadOptions()
		await this.getThermometerData()
		this.updateChart()
	}

	async loadOptions() {
		this.loading = true
		const res = await api.get(
			'/api/thermometer/collections/options?options=company_name,calc_subject&short=true',
		)
		this.options = res.data.options
		this.loading = false
	}

	async getThermometerData() {
		this.loading = true
		const res = await api.get('/api/thermometer')
		this.rawData = res.data.thermometer
		this.loading = false
	}

	handleCompanySelection(newSelection: Array<{ name: string }>) {
		this.alert = false
		if (newSelection.length > this.maxCompanies) {
			newSelection = newSelection.slice(0, this.maxCompanies)
			this.selectedCompanies = newSelection
			this.alert = true
		}

		const currentCompanies = newSelection.map(
			(company) => company?.name || '',
		)
		this.companyColors.forEach((_, companyName) => {
			if (!currentCompanies.includes(companyName)) {
				this.companyColors.delete(companyName)
			}
		})

		this.updateChart()
	}

	handleSubjectSelection(newSelection: Array<{ name: string }>) {
		this.alert = false
		if (newSelection.length > this.maxSubjects) {
			newSelection = newSelection.slice(0, this.maxSubjects)
			this.selectedSubjects = newSelection
			this.alert = true
		}

		const currentSubjects = newSelection.map(
			(subject) => subject?.name || '',
		)
		this.subjectColors.forEach((_, subjectName) => {
			if (!currentSubjects.includes(subjectName)) {
				this.subjectColors.delete(subjectName)
			}
		})

		this.updateChart()
	}

	updateChart() {
		const ctx = (this.$refs.chart as HTMLCanvasElement)?.getContext('2d')
		if (!ctx) return

		if (this.chart) {
			this.chart.destroy()
		}

		if (
			this.selectedCompanies.length === 0 &&
			this.selectedSubjects.length === 0
		) {
			this.createEmptyChart()
			return
		}

		// 1. TODOS os anos que têm distribuição para as empresas ou teses SELECIONADAS
		const allRelevantYears = new Set<string>()

		this.selectedCompanies.forEach((company) => {
			const companyName = company?.name || ''
			this.rawData
				.filter((item) => item.company_name === companyName)
				.forEach((item) => {
					try {
						if (item.distribution_date) {
							const year = new Date(item.distribution_date)
								.getFullYear()
								.toString()
							if (
								parseInt(year) >= 1900 &&
								parseInt(year) <= 2100
							) {
								allRelevantYears.add(year)
							}
						}
					} catch (e) {
						console.warn('Data inválida:', item.distribution_date)
					}
				})
		})

		this.selectedSubjects.forEach((subject) => {
			const subjectName = subject?.name || ''
			this.rawData
				.filter((item) => item.calc_subject?.name === subjectName)
				.forEach((item) => {
					try {
						if (item.distribution_date) {
							const year = new Date(item.distribution_date)
								.getFullYear()
								.toString()
							if (
								parseInt(year) >= 1900 &&
								parseInt(year) <= 2100
							) {
								allRelevantYears.add(year)
							}
						}
					} catch (e) {
						console.warn('Data inválida:', item.distribution_date)
					}
				})
		})

		// 2. Converter para array e ordenar
		const displayYears = Array.from(allRelevantYears).sort(
			(a, b) => parseInt(a) - parseInt(b),
		)

		// 3. Se não houver nenhum ano com dados, mostra o ano atual
		if (displayYears.length === 0) {
			displayYears.push(new Date().getFullYear().toString())
		}

		// 4. datasets para cada empresa selecionada
		const companyDatasets = this.selectedCompanies.map((company) => {
			const companyName = company?.name || ''

			if (!this.companyColors.has(companyName)) {
				const colorIndex =
					this.companyColors.size % this.colorPalette.length
				this.companyColors.set(
					companyName,
					this.colorPalette[colorIndex],
				)
			}
			const companyColor = this.companyColors.get(companyName) as string

			const companyData = this.rawData.filter(
				(item) => item.company_name === companyName,
			)

			let cumulativeSum = 0
			const accumulatedData = displayYears.map((year) => {
				const yearlyCount = companyData.filter((item) => {
					try {
						return (
							new Date(item.distribution_date)
								.getFullYear()
								.toString() === year
						)
					} catch {
						return false
					}
				}).length

				cumulativeSum += yearlyCount
				return cumulativeSum
			})

			const tooltipData = displayYears.reduce(
				(acc, year, index) => {
					const items = companyData.filter((item) => {
						try {
							return (
								new Date(item.distribution_date)
									.getFullYear()
									.toString() === year
							)
						} catch {
							return false
						}
					})

					acc[year] = {
						dates: items.map((item) => item.distribution_date),
						subjects: items.map(
							(item) => item.calc_subject?.name || '',
						),
						accumulatedTotal: accumulatedData[index],
					}
					return acc
				},
				{} as Record<
					string,
					{
						dates: string[]
						subjects: string[]
						accumulatedTotal: number
					}
				>,
			)

			return {
				label: companyName,
				backgroundColor: `rgba(${companyColor}, 0.5)`,
				borderColor: `rgba(${companyColor}, 1)`,
				pointBackgroundColor: `rgba(${companyColor}, 1)`,
				pointBorderColor: '#fff',
				data: accumulatedData,
				fill: false,
				tension: 0.1,
				pointRadius: 3,
				pointHoverRadius: 6,
				tooltipData,
				borderDash: [], // Linha sólida para empresas
			}
		})

		// 5. datasets para cada tese selecionada
		const subjectDatasets = this.selectedSubjects.map((subject) => {
			const subjectName = subject?.name || ''

			if (!this.subjectColors.has(subjectName)) {
				const colorIndex =
					(this.companyColors.size + this.subjectColors.size) %
					this.colorPalette.length
				this.subjectColors.set(
					subjectName,
					this.colorPalette[colorIndex],
				)
			}
			const subjectColor = this.subjectColors.get(subjectName) as string

			const subjectData = this.rawData.filter(
				(item) => item.calc_subject?.name === subjectName,
			)

			let cumulativeSum = 0
			const accumulatedData = displayYears.map((year) => {
				const yearlyCount = subjectData.filter((item) => {
					try {
						return (
							new Date(item.distribution_date)
								.getFullYear()
								.toString() === year
						)
					} catch {
						return false
					}
				}).length

				cumulativeSum += yearlyCount
				return cumulativeSum
			})

			const tooltipData = displayYears.reduce(
				(acc, year, index) => {
					const items = subjectData.filter((item) => {
						try {
							return (
								new Date(item.distribution_date)
									.getFullYear()
									.toString() === year
							)
						} catch {
							return false
						}
					})

					acc[year] = {
						dates: items.map((item) => item.distribution_date),
						companies: items.map((item) => item.company_name || ''),
						accumulatedTotal: accumulatedData[index],
					}
					return acc
				},
				{} as Record<
					string,
					{
						dates: string[]
						companies: string[]
						accumulatedTotal: number
					}
				>,
			)

			return {
				label: subjectName,
				backgroundColor: `rgba(${subjectColor}, 0.5)`,
				borderColor: `rgba(${subjectColor}, 1)`,
				pointBackgroundColor: `rgba(${subjectColor}, 1)`,
				pointBorderColor: '#fff',
				data: accumulatedData,
				fill: false,
				tension: 0.1,
				pointRadius: 3,
				pointHoverRadius: 6,
				tooltipData,
				borderDash: [5, 5], // Linha pontilhada para teses
			}
		})

		// 6. Configuração do grid para Repercussão Geral
		const gridLineConfig = {
			color: (context: any) => {
				const year = displayYears[context.index]
				const hasEvent = this.repercussaoGeral.some(
					(event) =>
						new Date(event.date).getFullYear().toString() === year,
				)
				return hasEvent
					? 'rgba(102, 102, 102, 0.7)'
					: 'rgba(0, 0, 0, 0.1)'
			},
			lineWidth: (context: any) => {
				const year = displayYears[context.index]
				const hasEvent = this.repercussaoGeral.some(
					(event) =>
						new Date(event.date).getFullYear().toString() === year,
				)
				return hasEvent ? 3 : 1
			},
		}

		// 7. Cria o gráfico com os anos filtrados
		this.chart = new Chart(ctx, {
			type: 'line',
			data: {
				labels: displayYears,
				datasets: [...companyDatasets, ...subjectDatasets],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						type: 'category',
						title: { display: true, text: 'Período de análise' },
						grid: gridLineConfig,
					},
					y: {
						beginAtZero: true,
						title: {
							display: true,
							text: 'Teses acumuladas',
						},
						ticks: { stepSize: 1, precision: 0 },
					},
				},
				plugins: {
					tooltip: {
						callbacks: {
							beforeBody: (context) => {
								// Adicionar informações de Repercussão Geral antes dos outros tooltips
								const year = displayYears[context[0].dataIndex]
								const events = this.repercussaoGeral.filter(
									(event) =>
										new Date(event.date)
											.getFullYear()
											.toString() === year,
								)

								if (events.length > 0) {
									return [
										'📌 Repercussão Geral:',
										...events.map(
											(event) =>
												` • ${event.description}`,
										),
									]
								}
								return []
							},
							label: (tooltipItem) => {
								const dataset = tooltipItem.dataset
								const year = displayYears[tooltipItem.dataIndex]
								const tooltipInfo = (dataset as any)
									.tooltipData[year]

								if (!tooltipInfo) {
									return [
										`${dataset.label}: Nenhum dado neste ano`,
									]
								}

								const isCompanyDataset =
									this.selectedCompanies.some(
										(c) => c?.name === dataset.label,
									)

								const tooltipLines = [
									`${dataset.label}: ${tooltipInfo.accumulatedTotal} ${tooltipInfo.accumulatedTotal === 1 ? 'distribuição' : 'distribuições'} até ${year}`,
								]

								if (isCompanyDataset) {
									tooltipInfo.subjects.forEach(
										(subject: string, i: number) => {
											const date = new Date(
												tooltipInfo.dates[i],
											).toLocaleDateString()
											tooltipLines.push(
												`• ${date} - ${subject}`,
											)
										},
									)
								} else {
									tooltipInfo.companies.forEach(
										(company: string, i: number) => {
											const date = new Date(
												tooltipInfo.dates[i],
											).toLocaleDateString()
											tooltipLines.push(
												`• ${date} - ${company}`,
											)
										},
									)
								}

								return tooltipLines
							},
						},
					},
					// legend: {
					// 	labels: {
					// 		usePointStyle: true,
					// 		pointStyle: (context: any) => {
					// 			const chart = this.chart
					// 			if (
					// 				!chart ||
					// 				!chart.data.datasets[context.datasetIndex]
					// 			) {
					// 				return 'rect'
					// 			}
					// 			const dataset = chart.data.datasets[
					// 				context.datasetIndex
					// 			] as any
					// 			return dataset.borderDash &&
					// 				dataset.borderDash.length
					// 				? 'circle'
					// 				: 'rect'
					// 		},
					// 		padding: 20,
					// 		font: { size: 12 },
					// 		generateLabels: (chart) => {
					// 			const labels =
					// 				Chart.defaults.plugins.legend.labels.generateLabels(
					// 					chart,
					// 				)

					// 			const repercussaoLabel = {
					// 				text: 'Repercussão Geral',
					// 				fillStyle: 'rgba(102, 102, 102, 0.7)',
					// 				strokeStyle: 'rgba(102, 102, 102, 1)',
					// 				lineWidth: 3,
					// 				lineDash: [0],
					// 				hidden: false,
					// 				datasetIndex: -1,
					// 				pointStyle: 'dash',
					// 			}

					// 			return [...labels, repercussaoLabel]
					// 		},
					// 		onClick: (legendItem, legend) => {
					// 			//Não faz nada quando clica na Repercussão Geral
					// 			if (legendItem.datasetIndex === -1) {
					// 				return
					// 			}

					// 			const index = legendItem.datasetIndex
					// 			const chart = legend.chart
					// 			if (chart.isDatasetVisible(index)) {
					// 				chart.hide(index)
					// 				legendItem.hidden = true
					// 			} else {
					// 				chart.show(index)
					// 				legendItem.hidden = false
					// 			}
					// 		},
					// 	},
					// },
					legend: {
						labels: {
							usePointStyle: true,
							pointStyle: ((context: any) => {
								const chart = this.chart
								if (
									!chart ||
									!chart.data.datasets[context.datasetIndex]
								) {
									return 'rect' // Default to 'rect'
								}
								const dataset = chart.data.datasets[
									context.datasetIndex
								] as any
								return dataset.borderDash?.length
									? 'dash'
									: 'rect'
							}) as
								| 'line'
								| 'circle'
								| 'cross'
								| 'crossRot'
								| 'dash'
								| 'rect'
								| 'rectRounded'
								| 'rectRot'
								| 'star'
								| 'triangle'
								| _DeepPartialObject<HTMLCanvasElement>
								| _DeepPartialObject<ImageBitmap>
								| undefined,
							padding: 20,
							font: { size: 12 },
							generateLabels: (
								chart: Chart<'line'>,
							): LegendItem[] => {
								const labels =
									Chart.defaults.plugins.legend.labels.generateLabels(
										chart,
									)

								const repercussaoLabel: LegendItem = {
									text: 'Repercussão Geral',
									fillStyle: 'rgba(102, 102, 102, 0.7)',
									strokeStyle: 'rgba(102, 102, 102, 1)',
									lineWidth: 3,
									lineDash: [0],
									hidden: false,
									datasetIndex: -1,
									pointStyle: 'dash',
									fontColor: '#666',
									lineCap: 'butt',
									lineJoin: 'miter',
									textAlign: 'left',
									borderRadius: 0,
									rotation: 0,
								}

								return [...labels, repercussaoLabel]
							},
						},
						onClick: (
							_e: ChartEvent,
							legendItem: LegendItem,
							legend: LegendElement<'line'>,
						) => {
							const customLegend = legendItem as LegendItem & {
								datasetIndex?: number
							}
							if (
								customLegend.datasetIndex === undefined ||
								customLegend.datasetIndex === -1
							) {
								return
							}

							const index = customLegend.datasetIndex
							const chart = legend.chart
							const visibility = chart.isDatasetVisible(index)

							if (visibility) {
								chart.hide(index)
								;(customLegend as any).hidden = true
							} else {
								chart.show(index)
								;(customLegend as any).hidden = false
							}
						},
					},
				},
			},
		})
	}

	clearChart() {
		this.selectedCompanies = []
		this.selectedSubjects = []
		this.alert = false
		this.loading = false
		this.companyColors.clear()
		this.subjectColors.clear()
		this.createEmptyChart()
	}

	createEmptyChart() {
		const ctx = (this.$refs.chart as HTMLCanvasElement)?.getContext('2d')
		if (!ctx) return
		if (this.chart) {
			this.chart.destroy()
		}
		this.chart = new Chart(ctx, {
			type: 'line',
			data: { labels: [], datasets: [] },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					x: {
						type: 'category',
						grid: { display: true, drawOnChartArea: true },
						title: { display: true, text: 'Período de análise' },
					},
					y: {
						type: 'linear',
						grid: { display: true, drawOnChartArea: true },
						title: {
							display: true,
							text: 'Teses acumuladas',
						},
						ticks: { stepSize: 1, precision: 0 },
						beginAtZero: true,
					},
				},
			},
		})
	}

	beforeDestroy() {
		if (this.chart) {
			this.chart.destroy()
		}
	}
}
